import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Card, Dropdown, Grid, Icon, Spinner, Text } from "~/components-shared";
import { IntegrationTypeEnum, StatusStateEnum } from "~/types/enums";
import moment from "moment";
import mixpanel from "mixpanel-browser";
import styled, { useTheme } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { resolveMonthDays } from "~/lib/helpers";
import toastsActionCreators from "~/store/toasts/actions";
import trendReportsActionCreators from "~/store/trendReports/actions";
import trendReportSelectors from "~/store/trendReports/selectors";
import { TrendReportActionEnum } from "~/store/trendReports/types";
import integrationSelectors from "~/store/integrations/selectors";
import { useQueryState } from "~/hooks/common";
const StyledGrid = styled(Grid)`
  border-radius: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.shape.borderRadius.primary;
}};
`;
const TopicTrendsInfoCard = _ref2 => {
  let {
    ...rest
  } = _ref2;
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const historyReports = useSelector(trendReportSelectors.getHistoryReports);
  const [queryParams, setQueryParams] = useQueryState();
  const reportId = queryParams.reportId || historyReports[0]?.id;
  const report = historyReports.find(r => r.id === reportId);
  const sendStatus = useSelector(trendReportSelectors.getStatusBySendReport(reportId));
  const subscribed = useSelector(trendReportSelectors.getSubscribed);
  const subscribeStatus = useSelector(trendReportSelectors.getSubscribeStatus);
  const [hoverSubscribe, setHoverSubscribe] = useState(false);
  const scheduledDate = moment.utc("5:00pm", "hh:mma").weekday(4).local();
  const deliveryDate = moment().valueOf() > scheduledDate.valueOf() ? scheduledDate.add("7", "days") : scheduledDate; // Friday 10:00am
  const slackIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.SLACK));
  const historyError = useSelector(trendReportSelectors.getHistoryError);
  const subscribeError = useSelector(trendReportSelectors.getSubscribeError);
  const reportError = useSelector(trendReportSelectors.getErrorByReport(reportId));
  const sendError = useSelector(trendReportSelectors.getErrorBySendReport(reportId));
  const error = reportError || historyError || sendError || subscribeError;
  useEffect(() => {
    if (slackIntegration && subscribeStatus === StatusStateEnum.IDLE) {
      dispatch(trendReportsActionCreators.fetchSubscriptionStarted());
    }
  }, [dispatch, subscribeStatus, slackIntegration]);
  if (!historyReports.length || !report) {
    return null;
  }
  const handleSend = callback => {
    dispatch(trendReportsActionCreators.sendStarted(reportId, _ref3 => {
      let {
        data
      } = _ref3;
      if (data === reportId) {
        dispatch(toastsActionCreators.pushed({
          severity: "success",
          message: t("TrendsInfoCard.reportSent"),
          timeout: 10000
        }));
        callback();
      }
    }));
    mixpanel.track(`Topic Report – Send in Slack`, {
      reportId
    });
  };
  const handleSubscribe = () => {
    if (subscribed) {
      dispatch(trendReportsActionCreators.unsubscribeStarted());
      mixpanel.track(`Topic Report – Unsubscribe`);
    } else {
      dispatch(trendReportsActionCreators.subscribeStarted());
      mixpanel.track(`Topic Report – Subscribe`);
    }
  };
  const handleShareInSlack = () => {
    dispatch(trendReportsActionCreators.actionChanged(TrendReportActionEnum.SHARE_IN_SLACK, reportId));
    mixpanel.track(`Topic Report – Share in Slack`, {
      reportId
    });
  };
  const handleClickReport = id => {
    setQueryParams({
      reportId: id
    });
    mixpanel.track(`Topic Report – Select`, {
      reportId: id
    });
  };
  const gridTemplate = ["auto", "auto", "auto"];
  if (slackIntegration) {
    gridTemplate.push("160px", "2.7rem");
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 6
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(Card, _extends({
    color: "transparent",
    border: true
  }, rest), /*#__PURE__*/React.createElement(StyledGrid, {
    cols: 8,
    gap: 0,
    template: gridTemplate.join(" "),
    s: {
      pl: 6,
      pr: 4,
      py: 3
    },
    align: "stretch"
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 1
    }
  }, t("TrendsInfoCard.period")), /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomLeft",
    customHandler: (toggle, isOpen) => /*#__PURE__*/React.createElement(Button, {
      color: "translucent",
      s: {
        py: 1.5,
        pl: 4,
        pr: 2,
        ml: -3
      },
      onClick: toggle,
      outline: false
    }, /*#__PURE__*/React.createElement(Text, {
      display: "block",
      weight: "bold",
      fontSize: 4
    }, resolveMonthDays(report.dateFrom, report.dateTo, true), /*#__PURE__*/React.createElement(Icon, {
      src: "chevron",
      scale: 5,
      s: {
        ml: 1
      },
      rotate: isOpen ? 0 : 180
    })))
  }, _ref4 => {
    let {
      isOpen,
      onItemClick
    } = _ref4;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen,
      width: "200px"
    }, historyReports.map((item, index) => /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      key: item.id,
      onClick: () => {
        handleClickReport(index === 0 ? undefined : item.id);
        onItemClick();
      },
      selected: item.id === reportId
    }, /*#__PURE__*/React.createElement(Grid, {
      cols: 2,
      template: "1fr auto",
      fullWidth: true
    }, /*#__PURE__*/React.createElement(Text, {
      weight: "semiBold"
    }, resolveMonthDays(item.dateFrom, item.dateTo, true))))));
  })), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2.5
    }
  }, t("TrendsInfoCard.nextDeliveryDate")), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "bold",
    fontSize: 4
  }, deliveryDate.format("ddd, MMM Do [at] h:mma"))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2.5
    }
  }, t("TrendsInfoCard.deliveryChannel")), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "bold",
    fontSize: 4
  }, slackIntegration ? "Slack" : "Joyn")), slackIntegration && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 1.5
    }
  }, t("TrendsInfoCard.receiveInSlack")), /*#__PURE__*/React.createElement(Button, {
    onClick: handleSubscribe,
    onMouseEnter: () => setHoverSubscribe(true),
    onMouseLeave: () => setHoverSubscribe(false),
    loading: subscribeStatus === StatusStateEnum.LOADING,
    fontSize: 3,
    s: {
      pl: 0,
      pr: 2,
      py: 1.5
    },
    color: subscribed ? "translucent" : "primary",
    maxWidth: "140px",
    fullWidth: true
  }, subscribed && /*#__PURE__*/React.createElement(Icon, {
    src: "tick",
    s: {
      mr: 1,
      mt: -0.5
    }
  }), subscribed ? hoverSubscribe ? t("TrendsInfoCard.unsubscribe") : t("TrendsInfoCard.subscribed") : t("TrendsInfoCard.subscribe"))), slackIntegration && /*#__PURE__*/React.createElement(Grid.Cell, {
    align: "center"
  }, /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomLeft",
    display: "inline-block",
    handlerProps: {
      s: {
        p: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      scale: 5,
      src: "dots-vertical",
      color: colors.text.primary,
      display: "block"
    }),
    fullWidth: true
  }, _ref5 => {
    let {
      isOpen,
      onItemClick
    } = _ref5;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleSend(() => {
          onItemClick();
        });
      }
    }, sendStatus === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement(Spinner, {
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }) : /*#__PURE__*/React.createElement(Icon, {
      src: "send",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), t("TrendsInfoCard.sendToSlack")), /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleShareInSlack();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: "share",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), t("TrendsInfoCard.shareInSlack")));
  }))))));
};
export default TopicTrendsInfoCard;